// pages/Transactions.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { formatCurrency, formatLocalDate } from '../utils';
import ManageAccountsModal from '../components/ManageAccountsModal';
import AddLoanPaymentModal from '../components/AddLoanPaymentModal';
import { showToast } from '../components/Toast';

const Transactions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [balances, setBalances] = useState({
    cleared_balance: 0,
    uncleared_balance: 0,
    working_balance: 0,
    reconciled_count: 0,
    total_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [showLoanPaymentModal, setShowLoanPaymentModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newTransactionData, setNewTransactionData] = useState(null);

  // Get account from URL params
  const searchParams = new URLSearchParams(location.search);
  const accountParam = searchParams.get('account');
  const [selectedAccount, setSelectedAccount] = useState(accountParam || 'all');

  // Default to show all transactions (no date filter)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Update selected account when URL param changes
  useEffect(() => {
    if (accountParam) {
      setSelectedAccount(accountParam);
    } else if (location.pathname === '/transactions') {
      // Reset to 'all' when navigating to /transactions without params
      setSelectedAccount('all');
    }
  }, [accountParam, location.pathname]);

  useEffect(() => {
    fetchData();
  }, [selectedAccount, startDate, endDate]);

  // Helper function to recalculate balances for a specific account
  const recalculateBalances = (accountData, transactionsList) => {
    if (!accountData) {
      // For "all accounts" view, calculate from all transactions
      const clearedBalance = transactionsList
        .filter(t => t.is_reconciled)
        .reduce((sum, t) => sum + (t.type === 'income' ? t.amount : -t.amount), 0);
      const unclearedBalance = transactionsList
        .filter(t => !t.is_reconciled)
        .reduce((sum, t) => sum + (t.type === 'income' ? t.amount : -t.amount), 0);
      const reconciledCount = transactionsList.filter(t => t.is_reconciled).length;

      setBalances({
        cleared_balance: clearedBalance,
        uncleared_balance: unclearedBalance,
        working_balance: clearedBalance + unclearedBalance,
        reconciled_count: reconciledCount,
        total_count: transactionsList.length
      });
      return;
    }

    // For a specific account, use current_balance as cleared balance
    const clearedBalance = accountData.current_balance || 0;

    // Calculate uncleared balance from unreconciled transactions
    const unclearedBalance = transactionsList
      .filter(t => !t.is_reconciled)
      .reduce((sum, t) => {
        if (t.type === 'income') return sum + t.amount;
        if (t.type === 'expense') return sum - t.amount;
        // For loan payments, only count the principal as uncleared (not interest/escrow)
        if (t.type === 'payment' && t.debt_id) {
          return sum - (t.principal_amount || 0);
        }
        if (t.type === 'payment') return sum - t.amount;
        return sum;
      }, 0);

    const reconciledCount = transactionsList.filter(t => t.is_reconciled).length;
    const workingBalance = clearedBalance + unclearedBalance;

    setBalances({
      cleared_balance: clearedBalance,
      uncleared_balance: unclearedBalance,
      working_balance: workingBalance,
      reconciled_count: reconciledCount,
      total_count: transactionsList.length
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};

      // Only add dates if they're set
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      // Don't filter by account on backend - we'll do it client-side
      // This allows us to show payment transactions on both bank and credit card accounts
      // (payment transactions have account_id = bank account but debt_id = credit card)

      // Fetch budgets for current month
      const currentMonth = new Date().toISOString().substring(0, 7);

      const [transactionsResponse, accountsResponse, debtsResponse, budgetsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API_BASE_PATH}/transactions`, { params }),
        axios.get(`${API_BASE_PATH}/accounts`),
        axios.get(`${API_BASE_PATH}/debts`),
        axios.get(`${API_BASE_PATH}/budget-items`, { params: { month: currentMonth } }),
        axios.get(`${API_BASE_PATH}/budget-categories`)
      ]);

      setTransactions(transactionsResponse.data || []);
      setAccounts(accountsResponse.data || []);
      setDebts(debtsResponse.data || []);
      setCategories(categoriesResponse.data || []);

      const allDebts = debtsResponse.data || [];
      const dbBudgets = (budgetsResponse.data || []).filter(b => b.type === 'budget');

      // Create virtual budgets for credit cards
      const creditCardDebts = allDebts.filter(d => d.type === 'credit_card');
      const creditCardVirtualBudgets = creditCardDebts.map(debt => {
        // Check if there's a real budget for this credit card
        const existingBudget = dbBudgets.find(b => b.debt_id === debt.id && b.category_id === 'virtual-credit-cards');

        return {
          id: existingBudget?.id || `virtual-cc-${debt.id}`,
          name: debt.name,
          category_id: 'virtual-credit-cards',
          type: 'budget',
          debt_id: debt.id,
          isVirtual: !existingBudget
        };
      });

      // Filter out credit card budgets - they should be handled through Payment transactions, not budget assignments
      const nonCreditCardBudgets = dbBudgets.filter(b => b.category_id !== 'virtual-credit-cards' && !b.debt_id);

      // Don't include virtual credit card budgets - payments to credit cards are handled via Payment type transactions
      setBudgets(nonCreditCardBudgets);

      // Calculate balances for the selected account
      const allTransactions = transactionsResponse.data || [];

      // Filter transactions for selected account
      const accountTransactions = selectedAccount && selectedAccount !== 'all'
        ? allTransactions.filter(t => t.account_id === selectedAccount || t.debt_id === selectedAccount)
        : allTransactions;

      // Get account data for balance calculation
      const accountData = selectedAccount && selectedAccount !== 'all'
        ? (accountsResponse.data?.find(a => a.id === selectedAccount) || allDebts.find(d => d.id === selectedAccount))
        : null;

      recalculateBalances(accountData, accountTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showToast('Failed to load transactions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = () => {
    // Initialize new transaction with defaults
    const defaultAccount = selectedAccount !== 'all' ? selectedAccount : (accounts[0]?.id || '');

    // Check if default account is a debt
    const isDebt = debts.find(d => d.id === defaultAccount);

    setNewTransactionData({
      account_id: defaultAccount,
      type: isDebt ? 'expense' : 'expense',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      budget_id: '',
      merchant: '',
      notes: '',
      is_reconciled: false,
      payment_account_id: '', // For credit card payments
      debt_id: isDebt ? defaultAccount : null
    });
    setIsAddingNew(true);
  };

  const handleEditTransaction = (transaction) => {
    setEditingId(transaction.id);

    // For payment transactions, we need to restore the proper account_id structure for editing
    // The transaction.debt_id contains the credit card, and transaction.account_id is the payment source
    let editData = { ...transaction };
    if (transaction.type === 'payment' && transaction.debt_id) {
      editData = {
        ...transaction,
        account_id: transaction.debt_id, // Show credit card as the "account"
        payment_account_id: transaction.account_id // Show bank account as payment source
      };
    }

    setEditingData(editData);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
    setIsAddingNew(false);
    setNewTransactionData(null);
  };

  const handleEditChange = (field, value) => {
    if (isAddingNew) {
      setNewTransactionData(prev => ({ ...prev, [field]: value }));
    } else {
      setEditingData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSaveEdit = async () => {
    try {
      const data = isAddingNew ? newTransactionData : editingData;

      // Validate required fields
      if (!data.account_id || !data.type || !data.amount || !data.date) {
        showToast('Please fill in all required fields', 'error');
        return;
      }

      // For payment transactions, validate payment_account_id
      if (data.type === 'payment' && !data.payment_account_id) {
        showToast('Please select an account to pay from', 'error');
        return;
      }

      // Check if the account is a debt (any type)
      const isDebt = debts.find(d => d.id === data.account_id);

      // Auto-generate description for payment transactions
      let description = data.description?.trim() || null;
      if (data.type === 'payment' && isDebt && !description) {
        const paymentAccount = accounts.find(a => a.id === data.payment_account_id);
        const debt = debts.find(d => d.id === data.account_id);
        description = `Payment to ${debt?.name || 'debt'} from ${paymentAccount?.name || 'account'}`;
      }

      const payload = {
        account_id: data.type === 'payment' && isDebt ? data.payment_account_id : data.account_id,
        type: data.type,
        amount: parseFloat(data.amount),
        date: data.date,
        description: description,
        budget_id: data.budget_id || null,
        merchant: data.merchant?.trim() || null,
        notes: data.notes?.trim() || null,
        is_reconciled: data.is_reconciled || false,
        reconciled_date: data.is_reconciled ? new Date().toISOString().split('T')[0] : null,
        debt_id: (data.type === 'payment' && isDebt) ? data.account_id : (data.debt_id || null)
      };

      if (isAddingNew) {
        // Create new transaction
        const response = await axios.post(`${API_BASE_PATH}/transactions`, payload);
        let savedTransaction = response.data || { ...payload, id: Date.now().toString() };

        // Add payment_account_id to the returned transaction for UI purposes
        if (data.type === 'payment' && isDebt) {
          savedTransaction = {
            ...savedTransaction,
            payment_account_id: data.payment_account_id
          };
        }

        const updatedTransactions = [savedTransaction, ...transactions];
        setTransactions(updatedTransactions);

        // Recalculate balances for the selected account
        const accountTransactions = selectedAccount && selectedAccount !== 'all'
          ? updatedTransactions.filter(t => t.account_id === selectedAccount || t.debt_id === selectedAccount)
          : updatedTransactions;
        recalculateBalances(selectedAccountData, accountTransactions);

        showToast('Transaction added', 'success');
        setIsAddingNew(false);
        setNewTransactionData(null);
      } else {
        // Update existing transaction
        const response = await axios.put(`${API_BASE_PATH}/transactions/${editingId}`, payload);
        let savedTransaction = response.data || { ...editingData, ...payload };

        // Add payment_account_id to the returned transaction for UI purposes
        if (data.type === 'payment' && isDebt) {
          savedTransaction = {
            ...savedTransaction,
            payment_account_id: data.payment_account_id
          };
        }

        const updatedTransactions = transactions.map(t =>
          t.id === editingId ? savedTransaction : t
        );
        setTransactions(updatedTransactions);

        // Recalculate balances for the selected account
        const accountTransactions = selectedAccount && selectedAccount !== 'all'
          ? updatedTransactions.filter(t => t.account_id === selectedAccount || t.debt_id === selectedAccount)
          : updatedTransactions;
        recalculateBalances(selectedAccountData, accountTransactions);

        showToast('Transaction updated', 'success');
        setEditingId(null);
        setEditingData(null);
      }
    } catch (error) {
      console.error('Error saving transaction:', error);
      showToast('Failed to save transaction', 'error');
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!confirm('Delete this transaction?')) return;

    try {
      await axios.delete(`${API_BASE_PATH}/transactions/${id}`);

      // Update state directly without page reload
      const updatedTransactions = transactions.filter(t => t.id !== id);
      setTransactions(updatedTransactions);

      // Recalculate balances for the selected account
      const accountTransactions = selectedAccount && selectedAccount !== 'all'
        ? updatedTransactions.filter(t => t.account_id === selectedAccount || t.debt_id === selectedAccount)
        : updatedTransactions;
      recalculateBalances(selectedAccountData, accountTransactions);

      showToast('Transaction deleted', 'success');
    } catch (error) {
      console.error('Error deleting transaction:', error);
      showToast('Failed to delete transaction', 'error');
    }
  };

  const handleToggleReconciled = async (transaction) => {
    const newReconciledState = !transaction.is_reconciled;

    try {
      await axios.put(`${API_BASE_PATH}/transactions/${transaction.id}`, {
        is_reconciled: newReconciledState,
        reconciled_date: newReconciledState ? new Date().toISOString().split('T')[0] : null
      });

      // Update state directly without page reload
      const updatedTransactions = transactions.map(t =>
        t.id === transaction.id
          ? { ...t, is_reconciled: newReconciledState, reconciled_date: newReconciledState ? new Date().toISOString().split('T')[0] : null }
          : t
      );
      setTransactions(updatedTransactions);

      // Track updated accounts and debts for balance recalculation
      let updatedAccounts = [...accounts];
      let updatedDebts = [...debts];

      // Update account current_balance when transaction is reconciled/unreconciled
      const accountId = transaction.account_id;
      const accountToUpdate = accounts.find(a => a.id === accountId) || debts.find(d => d.id === accountId);

      if (accountToUpdate) {
        // Calculate the balance change
        let balanceChange = 0;
        if (transaction.type === 'income') {
          balanceChange = transaction.amount;
        } else if (transaction.type === 'expense' || transaction.type === 'payment') {
          balanceChange = -transaction.amount;
        }

        // Apply or reverse the balance change
        const newBalance = newReconciledState
          ? accountToUpdate.current_balance + balanceChange  // Reconciling: add to balance
          : accountToUpdate.current_balance - balanceChange; // Unreconciling: subtract from balance

        // Update the account balance in the database
        const updateEndpoint = debts.find(d => d.id === accountId)
          ? `${API_BASE_PATH}/debts/${accountId}`
          : `${API_BASE_PATH}/accounts/${accountId}`;

        await axios.put(updateEndpoint, {
          current_balance: newBalance
        });

        // Update local state
        if (debts.find(d => d.id === accountId)) {
          updatedDebts = debts.map(d => d.id === accountId ? { ...d, current_balance: newBalance } : d);
          setDebts(updatedDebts);
        } else {
          updatedAccounts = accounts.map(a => a.id === accountId ? { ...a, current_balance: newBalance } : a);
          setAccounts(updatedAccounts);
        }
      }

      // For payment transactions, also update the debt account balance
      if (transaction.type === 'payment' && transaction.debt_id) {
        const debtToUpdate = updatedDebts.find(d => d.id === transaction.debt_id);

        if (debtToUpdate) {
          // For loan payments, only the principal reduces the debt balance (not interest or escrow)
          const principalAmount = transaction.principal_amount || transaction.amount;
          const debtBalanceChange = -principalAmount;

          const newDebtBalance = newReconciledState
            ? debtToUpdate.current_balance + debtBalanceChange  // Reconciling: reduce debt by principal
            : debtToUpdate.current_balance - debtBalanceChange; // Unreconciling: add back principal

          // For mortgages, also update escrow balance
          const updatePayload = { current_balance: newDebtBalance };
          if (debtToUpdate.type === 'mortgage' && transaction.escrow_amount) {
            const escrowChange = newReconciledState ? transaction.escrow_amount : -transaction.escrow_amount;
            updatePayload.escrow_balance = (debtToUpdate.escrow_balance || 0) + escrowChange;
          }

          await axios.put(`${API_BASE_PATH}/debts/${transaction.debt_id}`, updatePayload);

          // Update local state
          updatedDebts = updatedDebts.map(d => d.id === transaction.debt_id ? { ...d, ...updatePayload } : d);
          setDebts(updatedDebts);
        }
      }

      // Recalculate balances for the selected account using updated data
      if (selectedAccount && selectedAccount !== 'all') {
        const currentAccountData = updatedAccounts.find(a => a.id === selectedAccount) || updatedDebts.find(d => d.id === selectedAccount);
        const accountTransactions = updatedTransactions.filter(t => t.account_id === selectedAccount || t.debt_id === selectedAccount);
        recalculateBalances(currentAccountData, accountTransactions);
      } else {
        recalculateBalances(null, updatedTransactions);
      }

      showToast(`Transaction ${newReconciledState ? 'reconciled' : 'unreconciled'}`, 'success');
    } catch (error) {
      console.error('Error toggling reconciled:', error);
      showToast('Failed to update transaction', 'error');
    }
  };

  const handleBudgetChange = async (transaction, budgetId) => {
    try {
      await axios.put(`${API_BASE_PATH}/transactions/${transaction.id}`, {
        budget_id: budgetId || null
      });

      // Update state directly without page reload
      const updatedTransactions = transactions.map(t =>
        t.id === transaction.id
          ? { ...t, budget_id: budgetId || null }
          : t
      );
      setTransactions(updatedTransactions);

      showToast('Budget assignment updated', 'success');
    } catch (error) {
      console.error('Error updating budget:', error);
      showToast('Failed to update budget assignment', 'error');
    }
  };

  const filteredTransactions = transactions.filter(t => {
    // First apply account filter if we're viewing a specific account
    // This is needed because payment transactions might not be captured by backend filtering
    if (selectedAccount && selectedAccount !== 'all') {
      const matchesAccount = t.account_id === selectedAccount;
      const matchesDebt = t.debt_id === selectedAccount;

      // Show transaction if either the account_id or debt_id matches
      // This ensures payment transactions show up on both the bank account and credit card views
      if (!matchesAccount && !matchesDebt) {
        return false;
      }
    }

    // Then apply search filter
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      t.description?.toLowerCase().includes(search) ||
      t.merchant?.toLowerCase().includes(search) ||
      t.notes?.toLowerCase().includes(search)
    );
  });

  // Calculate available balance for selected account
  const selectedAccountData = selectedAccount !== 'all'
    ? (accounts.find(a => a.id === selectedAccount) || debts.find(d => d.id === selectedAccount))
    : null;

  // Check if selected account is a loan (not credit card)
  const isLoanAccount = selectedAccountData && debts.find(d => d.id === selectedAccount && d.type !== 'credit_card');

  // Calculate total principal and interest paid for loan accounts
  const totalPrincipalPaid = isLoanAccount
    ? filteredTransactions
        .filter(t => t.type === 'payment' && t.is_reconciled)
        .reduce((sum, t) => sum + (t.principal_amount || 0), 0)
    : 0;

  const totalInterestPaid = isLoanAccount
    ? filteredTransactions
        .filter(t => t.type === 'payment' && t.is_reconciled)
        .reduce((sum, t) => sum + (t.interest_amount || 0), 0)
    : 0;

  const getAvailableBalance = (account) => {
    if (!account) return null;

    // Get all unreconciled transactions for this account
    const unreconciledTransactions = transactions.filter(
      t => t.account_id === account.id && !t.is_reconciled
    );

    // Calculate pending amount
    const pendingAmount = unreconciledTransactions.reduce((sum, t) => {
      if (t.type === 'income') {
        return sum + t.amount;
      } else if (t.type === 'expense') {
        return sum - t.amount;
      }
      return sum;
    }, 0);

    return account.current_balance - pendingAmount;
  };

  const availableBalance = selectedAccountData ? getAvailableBalance(selectedAccountData) : null;

  // Calculate combined balance for all bank accounts (excluding credit cards and loans)
  const getTotalBalance = () => {
    // Filter out accounts that are debts (credit cards, loans, etc.)
    const bankAccountsOnly = accounts.filter(a => !debts.some(d => d.id === a.id));
    const totalAccounts = bankAccountsOnly.reduce((sum, a) => sum + (a.current_balance || 0), 0);
    return totalAccounts;
  };

  const getTotalAvailable = () => {
    let total = 0;
    // Only include bank accounts, not credit cards or loans
    const bankAccountsOnly = accounts.filter(a => !debts.some(d => d.id === a.id));
    bankAccountsOnly.forEach(account => {
      const available = getAvailableBalance(account);
      if (available !== null) {
        total += available;
      }
    });
    return total;
  };

  const getAccountName = (accountId) => {
    // Check regular accounts first
    const account = accounts.find(a => a.id === accountId);
    if (account) return account.name;

    // Check debt accounts
    const debt = debts.find(d => d.id === accountId);
    if (debt) return debt.name;

    return 'Unknown Account';
  };

  const getBudgetName = (budgetId) => {
    const budget = budgets.find(b => b.id === budgetId);
    return budget ? budget.name : null;
  };

  const getTypeIcon = (type) => {
    const icons = {
      income: 'fa-arrow-down',
      expense: 'fa-arrow-up',
      bill_payment: 'fa-file-invoice',
      debt_payment: 'fa-credit-card',
      payment: 'fa-credit-card',
      interest: 'fa-percentage',
      fee: 'fa-exclamation-circle',
      transfer: 'fa-exchange-alt'
    };
    return icons[type] || 'fa-circle';
  };

  const getTypeColor = (type) => {
    const colors = {
      income: 'var(--success-color)',
      expense: 'var(--danger-color)',
      bill_payment: 'var(--primary-color)',
      debt_payment: 'var(--warning-color)',
      payment: 'var(--warning-color)',
      interest: 'var(--danger-color)',
      fee: 'var(--danger-color)',
      transfer: 'var(--info-color)'
    };
    return colors[type] || 'var(--text-secondary)';
  };

  const getTypeLabel = (type) => {
    const labels = {
      income: 'Income',
      expense: 'Expense',
      bill_payment: 'Bill Payment',
      debt_payment: 'Debt Payment',
      payment: 'Payment',
      interest: 'Interest',
      fee: 'Fee',
      transfer: 'Transfer'
    };
    return labels[type] || type.replace('_', ' ');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>
            <i className="fas fa-exchange-alt" style={{ marginRight: '10px' }}></i>
            Transactions
            {selectedAccountData && (
              <span style={{ fontSize: '16px', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '12px' }}>
                {selectedAccountData.name}
              </span>
            )}
          </h1>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '2px 0 0 0' }}>
            Track all your financial transactions and reconcile with your accounts
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isLoanAccount && (
            <button
              className="btn btn-primary"
              onClick={() => setShowLoanPaymentModal(true)}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              <i className="fas fa-money-check-alt" style={{ marginRight: '8px' }}></i>
              Add Payment
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={handleAddTransaction}
            disabled={isAddingNew}
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>
            Add Transaction
          </button>
        </div>
      </div>
      {/* Compact Summary Card with Filters Dropdown */}
      <div style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--border-radius)',
        padding: '12px 16px',
        marginBottom: '16px',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', fontWeight: 600 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>Cleared</span>
              <span style={{ color: 'var(--success-color)', fontSize: '16px' }}>
                {formatCurrency(balances.cleared_balance)}
              </span>
            </div>
            <span style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>+</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>Uncleared</span>
              <span style={{ color: 'var(--warning-color)', fontSize: '16px' }}>
                {formatCurrency(balances.uncleared_balance)}
              </span>
            </div>
            <span style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>=</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>Working Balance</span>
              <span style={{
                color: balances.working_balance >= 0 ? 'var(--primary-color)' : 'var(--danger-color)',
                fontSize: '18px',
                fontWeight: 700
              }}>
                {formatCurrency(balances.working_balance)}
              </span>
            </div>
            {isLoanAccount && (
              <>
                <span style={{ color: 'var(--border-color)', fontSize: '18px', margin: '0 8px' }}>|</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>Total Principal</span>
                  <span style={{ color: 'var(--success-color)', fontSize: '16px' }}>
                    {formatCurrency(totalPrincipalPaid)}
                  </span>
                </div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>•</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>Total Interest</span>
                  <span style={{ color: 'var(--danger-color)', fontSize: '16px' }}>
                    {formatCurrency(totalInterestPaid)}
                  </span>
                </div>
                {selectedAccountData?.type === 'mortgage' && (
                  <>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>•</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>Escrow</span>
                      <span style={{ color: 'var(--primary-color)', fontSize: '16px' }}>
                        {formatCurrency(selectedAccountData?.escrow_balance || 0)}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              paddingRight: '12px',
              borderRight: '1px solid var(--border-color)'
            }}>
              <i className="fas fa-check-circle" style={{ color: 'var(--success-color)' }}></i>
              <span>{balances.reconciled_count} of {balances.total_count} reconciled</span>
            </div>
            <button
              onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--border-radius)',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <i className="fas fa-filter"></i>
              Filters
              <i className={`fas fa-chevron-${showFiltersDropdown ? 'up' : 'down'}`} style={{ fontSize: '10px' }}></i>
            </button>
          </div>
        </div>

        {/* Filters Dropdown */}
        {showFiltersDropdown && (
          <div style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'end'
          }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 150px' }}>
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 150px' }}>
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="No limit"
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 250px' }}>
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search description, merchant, notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Transactions Table */}
      {filteredTransactions.length === 0 && !isAddingNew ? (
        <div className="empty-state">
          <i className="fas fa-receipt empty-state-icon"></i>
          <h3 className="empty-state-title">No transactions found</h3>
          <p className="empty-state-description">
            {transactions.length === 0
              ? 'Start tracking your finances by adding your first transaction'
              : 'No transactions match your current filters'}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th>Account</th>
                <th>Budget</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* New Transaction Row */}
              {isAddingNew && newTransactionData && (() => {
                const selectedDebt = debts.find(d => d.id === newTransactionData.account_id);
                const isDebt = !!selectedDebt;

                return (
                <tr style={{ background: 'var(--primary-light)' }}>
                  <td>
                    <input
                      type="date"
                      className="form-control"
                      style={{ fontSize: '12px', padding: '4px 8px', minWidth: '120px' }}
                      value={newTransactionData.date}
                      onChange={(e) => handleEditChange('date', e.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      className="form-control"
                      style={{ fontSize: '12px', padding: '4px 8px', minWidth: '100px' }}
                      value={newTransactionData.type}
                      onChange={(e) => handleEditChange('type', e.target.value)}
                    >
                      <option value="expense">Expense</option>
                      <option value="income">Income</option>
                      {isDebt && <option value="payment">Payment</option>}
                    </select>
                  </td>
                  <td>
                    {newTransactionData.type === 'payment' ? (
                      <select
                        className="form-control"
                        style={{ fontSize: '12px', padding: '4px 8px', minWidth: '150px' }}
                        value={newTransactionData.payment_account_id || ''}
                        onChange={(e) => handleEditChange('payment_account_id', e.target.value)}
                      >
                        <option value="">Select payment account...</option>
                        {accounts.filter(a => a.is_active && !debts.some(d => d.id === a.id)).map(account => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        style={{ fontSize: '12px', padding: '4px 8px', minWidth: '150px' }}
                        placeholder="Description"
                        value={newTransactionData.description}
                        onChange={(e) => handleEditChange('description', e.target.value)}
                        autoFocus
                      />
                    )}
                  </td>
                  <td>
                    <select
                      className="form-control"
                      style={{ fontSize: '12px', padding: '4px 8px', minWidth: '120px' }}
                      value={newTransactionData.account_id}
                      onChange={(e) => {
                        const newAccountId = e.target.value;
                        const isNewDebt = debts.find(d => d.id === newAccountId);
                        handleEditChange('account_id', newAccountId);
                        // Update debt_id if switching to/from debt
                        handleEditChange('debt_id', isNewDebt ? newAccountId : null);
                        // Reset payment account when switching accounts
                        if (newTransactionData.type === 'payment') {
                          handleEditChange('payment_account_id', '');
                        }
                      }}
                    >
                      {accounts.filter(a => a.is_active && !debts.some(d => d.id === a.id)).map(account => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                      {debts.length > 0 && (
                        <optgroup label="Debts">
                          {debts.map(debt => (
                            <option key={debt.id} value={debt.id}>
                              {debt.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </td>
                  <td>
                    {newTransactionData.type === 'expense' && !isDebt ? (
                      <select
                        className="form-control"
                        style={{ fontSize: '12px', padding: '4px 8px', minWidth: '150px' }}
                        value={newTransactionData.budget_id || ''}
                        onChange={(e) => handleEditChange('budget_id', e.target.value)}
                      >
                        <option value="">Not budgeted</option>
                        {(() => {
                          const sortedCategories = [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

                          const grouped = sortedCategories.map(category => {
                            const categoryBudgets = budgets.filter(b => b.category_id === category.id);
                            if (categoryBudgets.length === 0) return null;

                            return (
                              <optgroup key={category.id} label={category.name}>
                                {categoryBudgets.map(budget => (
                                  <option key={budget.id} value={budget.id}>
                                    {budget.name}
                                  </option>
                                ))}
                              </optgroup>
                            );
                          }).filter(Boolean);

                          const uncategorizedBudgets = budgets.filter(b => !b.category_id);
                          if (uncategorizedBudgets.length > 0) {
                            grouped.push(
                              <optgroup key="uncategorized" label="Uncategorized">
                                {uncategorizedBudgets.map(budget => (
                                  <option key={budget.id} value={budget.id}>
                                    {budget.name}
                                  </option>
                                ))}
                              </optgroup>
                            );
                          }

                          return grouped;
                        })()}
                      </select>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        {isDebt && newTransactionData.type === 'expense' ? 'Debt' : 'N/A'}
                      </span>
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      style={{ fontSize: '12px', padding: '4px 8px', minWidth: '100px' }}
                      placeholder="0.00"
                      step="0.01"
                      value={newTransactionData.amount}
                      onChange={(e) => handleEditChange('amount', e.target.value)}
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => handleEditChange('is_reconciled', !newTransactionData.is_reconciled)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: newTransactionData.is_reconciled ? 'var(--success-color)' : 'var(--text-secondary)',
                        opacity: newTransactionData.is_reconciled ? 1 : 0.3
                      }}
                    >
                      <i className={newTransactionData.is_reconciled ? 'fas fa-check-circle' : 'far fa-circle'}></i>
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn-table-action"
                        onClick={handleSaveEdit}
                        title="Save transaction"
                      >
                        <i className="fas fa-check"></i>
                      </button>
                      <button
                        className="btn-table-action danger"
                        onClick={handleCancelEdit}
                        title="Cancel"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })()}

              {/* Existing Transactions */}
              {filteredTransactions.map(transaction => {
                const isEditing = editingId === transaction.id;
                const data = isEditing ? editingData : transaction;
                const selectedDebt = isEditing ? debts.find(d => d.id === data.account_id) : null;
                const isDebt = !!selectedDebt;

                return (
                <tr key={transaction.id} style={isEditing ? { background: 'var(--primary-light)' } : {}}>
                  <td>
                    {isEditing ? (
                      <input
                        type="date"
                        className="form-control"
                        style={{ fontSize: '12px', padding: '4px 8px', minWidth: '120px' }}
                        value={data.date}
                        onChange={(e) => handleEditChange('date', e.target.value)}
                      />
                    ) : (
                      <strong>{formatLocalDate(transaction.date)}</strong>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        className="form-control"
                        style={{ fontSize: '12px', padding: '4px 8px', minWidth: '100px' }}
                        value={data.type}
                        onChange={(e) => handleEditChange('type', e.target.value)}
                      >
                        <option value="expense">Expense</option>
                        <option value="income">Income</option>
                        {isDebt && <option value="payment">Payment</option>}
                      </select>
                    ) : (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: getTypeColor(transaction.type)
                      }}>
                        <i className={`fas ${getTypeIcon(transaction.type)}`}></i>
                        {getTypeLabel(transaction.type)}
                      </span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      data.type === 'payment' ? (
                        <select
                          className="form-control"
                          style={{ fontSize: '12px', padding: '4px 8px', minWidth: '150px' }}
                          value={data.payment_account_id || ''}
                          onChange={(e) => handleEditChange('payment_account_id', e.target.value)}
                        >
                          <option value="">Select payment account...</option>
                          {accounts.filter(a => a.is_active && !debts.some(d => d.id === a.id)).map(account => (
                            <option key={account.id} value={account.id}>
                              {account.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          style={{ fontSize: '12px', padding: '4px 8px', minWidth: '150px' }}
                          placeholder="Description"
                          value={data.description || ''}
                          onChange={(e) => handleEditChange('description', e.target.value)}
                        />
                      )
                    ) : (
                      <div>
                        <strong>{transaction.description || 'No description'}</strong>
                        {transaction.merchant && (
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {transaction.merchant}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        className="form-control"
                        style={{ fontSize: '12px', padding: '4px 8px', minWidth: '120px' }}
                        value={data.account_id}
                        onChange={(e) => {
                          const newAccountId = e.target.value;
                          const isNewDebt = debts.find(d => d.id === newAccountId);
                          handleEditChange('account_id', newAccountId);
                          handleEditChange('debt_id', isNewDebt ? newAccountId : null);
                          if (data.type === 'payment') {
                            handleEditChange('payment_account_id', '');
                          }
                        }}
                      >
                        {accounts.filter(a => a.is_active && !debts.some(d => d.id === a.id)).map(account => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))}
                        {debts.length > 0 && (
                          <optgroup label="Debts">
                            {debts.map(debt => (
                              <option key={debt.id} value={debt.id}>
                                {debt.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    ) : (
                      getAccountName(transaction.account_id)
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      data.type === 'expense' && !isDebt ? (
                        <select
                          className="form-control"
                          style={{ fontSize: '12px', padding: '4px 8px', minWidth: '150px' }}
                          value={data.budget_id || ''}
                          onChange={(e) => handleEditChange('budget_id', e.target.value)}
                        >
                          <option value="">Not budgeted</option>
                          {(() => {
                            const sortedCategories = [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

                            const grouped = sortedCategories.map(category => {
                              const categoryBudgets = budgets.filter(b => b.category_id === category.id);
                              if (categoryBudgets.length === 0) return null;

                              return (
                                <optgroup key={category.id} label={category.name}>
                                  {categoryBudgets.map(budget => (
                                    <option key={budget.id} value={budget.id}>
                                      {budget.name}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            }).filter(Boolean);

                            const uncategorizedBudgets = budgets.filter(b => !b.category_id);
                            if (uncategorizedBudgets.length > 0) {
                              grouped.push(
                                <optgroup key="uncategorized" label="Uncategorized">
                                  {uncategorizedBudgets.map(budget => (
                                    <option key={budget.id} value={budget.id}>
                                      {budget.name}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            }

                            return grouped;
                          })()}
                        </select>
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                          {isDebt && data.type === 'expense' ? 'Debt' : 'N/A'}
                        </span>
                      )
                    ) : (
                      transaction.type === 'expense' ? (
                        transaction.debt_id ? (
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            Debt
                          </span>
                        ) : (
                          <select
                            className="form-control"
                            style={{
                              fontSize: '12px',
                              padding: '4px 8px',
                              minWidth: '150px',
                              background: transaction.budget_id ? 'var(--bg-tertiary)' : 'transparent',
                              border: transaction.budget_id ? '1px solid var(--border-color)' : '1px dashed var(--border-color)',
                              cursor: 'pointer'
                            }}
                            value={transaction.budget_id || ''}
                            onChange={(e) => handleBudgetChange(transaction, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">Not budgeted</option>
                            {budgets.length === 0 ? (
                              <option value="" disabled>No budgets available</option>
                            ) : (
                              (() => {
                                const sortedCategories = [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

                                const grouped = sortedCategories.map(category => {
                                  const categoryBudgets = budgets.filter(b => b.category_id === category.id);
                                  if (categoryBudgets.length === 0) return null;

                                  return (
                                    <optgroup key={category.id} label={category.name}>
                                      {categoryBudgets.map(budget => (
                                        <option key={budget.id} value={budget.id}>
                                          {budget.name}
                                        </option>
                                      ))}
                                    </optgroup>
                                  );
                                }).filter(Boolean);

                                const uncategorizedBudgets = budgets.filter(b => !b.category_id);
                                if (uncategorizedBudgets.length > 0) {
                                  grouped.push(
                                    <optgroup key="uncategorized" label="Uncategorized">
                                      {uncategorizedBudgets.map(budget => (
                                        <option key={budget.id} value={budget.id}>
                                          {budget.name}
                                        </option>
                                      ))}
                                    </optgroup>
                                  );
                                }

                                return grouped;
                              })()
                            )}
                          </select>
                        )
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                          N/A
                        </span>
                      )
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        className="form-control"
                        style={{ fontSize: '12px', padding: '4px 8px', minWidth: '100px' }}
                        placeholder="0.00"
                        step="0.01"
                        value={data.amount}
                        onChange={(e) => handleEditChange('amount', e.target.value)}
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <strong style={{ color: transaction.type === 'income' ? 'var(--success-color)' : 'var(--danger-color)' }}>
                          {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                        </strong>
                        {transaction.type === 'payment' && transaction.debt_id && (transaction.principal_amount || transaction.interest_amount) && (
                          <div
                            className="loan-payment-info"
                            style={{ position: 'relative', display: 'inline-block' }}
                          >
                            <i
                              className="fas fa-info-circle"
                              style={{
                                color: 'var(--primary-color)',
                                fontSize: '12px',
                                cursor: 'help'
                              }}
                              title="Loan Payment Details"
                            ></i>
                            <div className="loan-payment-tooltip">
                              <div style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }}>
                                Loan Payment Breakdown
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: 'var(--text-secondary)' }}>
                                <span>Principal:</span>
                                <span style={{ fontWeight: 600, color: 'var(--success-color)' }}>
                                  {formatCurrency(transaction.principal_amount || 0)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: 'var(--text-secondary)' }}>
                                <span>Interest:</span>
                                <span style={{ fontWeight: 600, color: 'var(--danger-color)' }}>
                                  {formatCurrency(transaction.interest_amount || 0)}
                                </span>
                              </div>
                              {transaction.escrow_amount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: 'var(--text-secondary)' }}>
                                  <span>Escrow:</span>
                                  <span style={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                                    {formatCurrency(transaction.escrow_amount)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <button
                        onClick={() => handleEditChange('is_reconciled', !data.is_reconciled)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: data.is_reconciled ? 'var(--success-color)' : 'var(--text-secondary)',
                          opacity: data.is_reconciled ? 1 : 0.3
                        }}
                      >
                        <i className={data.is_reconciled ? 'fas fa-check-circle' : 'far fa-circle'}></i>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleToggleReconciled(transaction)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: transaction.is_reconciled ? 'var(--success-color)' : 'var(--text-secondary)',
                          opacity: transaction.is_reconciled ? 1 : 0.3
                        }}
                        title={transaction.is_reconciled ? 'Reconciled' : 'Not reconciled'}
                      >
                        <i className={transaction.is_reconciled ? 'fas fa-check-circle' : 'far fa-circle'}></i>
                      </button>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn-table-action"
                          onClick={handleSaveEdit}
                          title="Save changes"
                        >
                          <i className="fas fa-check"></i>
                        </button>
                        <button
                          className="btn-table-action danger"
                          onClick={handleCancelEdit}
                          title="Cancel"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn-table-action"
                          onClick={() => handleEditTransaction(transaction)}
                          title="Edit transaction"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn-table-action danger"
                          onClick={() => handleDeleteTransaction(transaction.id)}
                          title="Delete transaction"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Manage Accounts Modal */}
      {showAccountsModal && (
        <ManageAccountsModal
          onClose={() => setShowAccountsModal(false)}
          onSave={() => {
            setShowAccountsModal(false);
            fetchData();
          }}
        />
      )}

      {/* Loan Payment Modal */}
      {showLoanPaymentModal && isLoanAccount && (
        <AddLoanPaymentModal
          debt={selectedAccountData}
          accounts={accounts.filter(a => !debts.some(d => d.id === a.id))}
          onClose={() => setShowLoanPaymentModal(false)}
          onSave={() => {
            setShowLoanPaymentModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default Transactions;
